import { useState, useMemo, useEffect, type ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  Globe,
  HeartPulse,
  ScrollText,
  Layers,
  AlertTriangle,
  Plug,
  Globe2,
  Search,
  Database,
  Shield,
} from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { LiveStatusBar } from './LiveStatusBar'
import { LiveOverviewSection } from './LiveOverviewSection'
import { LiveHealthSection } from './LiveHealthSection'
import { LiveLogsSection } from './LiveLogsSection'
import { LiveScaleSection } from './LiveScaleSection'
import { LiveIntegrationsSection } from '../LiveIntegrationsSection'
import { DnsManagementSection } from '../DnsManagementSection'
import { VanitySubdomainSection } from '../VanitySubdomainSection'
import { ErrorsPane } from '../ErrorsPane'
import { LiveSeoSection } from './LiveSeoSection'
import { LiveSecurityAuditSection } from './LiveSecurityAuditSection'
import { LiveDatabaseSection } from './LiveDatabaseSection'
import type { Project, ActiveWorkflow } from '@/types/conversations'
import { isProductionWorkflowStarting } from '@/lib/workflow-utils'

type SectionId = 'overview' | 'health' | 'logs' | 'scale' | 'errors' | 'database' | 'seo' | 'security' | 'integrations' | 'dns'

interface SectionDef {
  id: SectionId
  icon: ComponentType<{ className?: string }>
  label: string
}

interface DjangoLiveSidebarProps {
  project: Project
  processing: boolean
  conversationId?: number
  onDeploy: () => void
  onRedeploy: () => void
  onRestart: () => void
  activeWorkflow?: ActiveWorkflow | null
  onAbortWorkflow?: () => void
  onRetryWorkflow?: () => Promise<void>
  isAbortingWorkflow?: boolean
  isRetryingWorkflow?: boolean
}

export function DjangoLiveSidebar({
  project,
  processing,
  conversationId,
  onDeploy,
  onRedeploy,
  onRestart,
  activeWorkflow,
  onAbortWorkflow,
  onRetryWorkflow,
  isAbortingWorkflow,
  isRetryingWorkflow,
}: DjangoLiveSidebarProps) {
  const { t } = useTranslation('conversations')
  const isMobile = useIsMobile()
  const domainConfigured = !!project.custom_domain

  const [activeSection, setActiveSection] = useState<SectionId>('overview')

  useEffect(() => {
    if (isProductionWorkflowStarting(activeWorkflow) && activeSection !== 'overview') {
      setActiveSection('overview')
    }
  }, [activeWorkflow?.id])

  const { sreSections, configSections, allSections } = useMemo(() => {
    const sre: SectionDef[] = []
    const config: SectionDef[] = []

    sre.push(
      { id: 'overview', icon: Globe, label: t('live.sre.labels.overview') },
    )

    // Domain second in SRE group if not yet configured, to prompt setup
    if (!domainConfigured && project.production_status === 'deployed') {
      sre.push({ id: 'dns', icon: Globe2, label: t('live.sre.labels.domain') })
    }

    sre.push(
      { id: 'health', icon: HeartPulse, label: t('live.sre.labels.health') },
      { id: 'logs', icon: ScrollText, label: t('live.sre.labels.logs') },
      { id: 'scale', icon: Layers, label: t('live.sre.labels.scale') },
      { id: 'errors', icon: AlertTriangle, label: t('live.sre.labels.errors') },
    )

    if (project.production_status === 'deployed') {
      sre.push({ id: 'database', icon: Database, label: t('live.sre.labels.database') })
    }

    if (project.production_status === 'deployed' && project.custom_domain) {
      sre.push({ id: 'seo', icon: Search, label: t('live.sre.labels.seo') })
    }

    if (project.production_status === 'deployed') {
      sre.push({ id: 'security', icon: Shield, label: t('live.sre.labels.security') })
    }

    if (project.integrations.filter(i => i.sandbox_variables.length > 0).length > 0) {
      config.push({ id: 'integrations', icon: Plug, label: t('live.sre.labels.integrations') })
    }

    // Domain last in config group once configured
    if (domainConfigured) {
      config.push({ id: 'dns', icon: Globe2, label: t('live.sre.labels.domain') })
    }

    return { sreSections: sre, configSections: config, allSections: [...sre, ...config] }
  }, [domainConfigured, project.production_status, project.integrations, t])


  const isSandboxStopped = project.runtime_state !== 'running'

  const renderContent = () => {
    if (isSandboxStopped) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-card">
          <div className="h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-4">
            <Globe className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h3 className="font-semibold text-lg text-foreground mb-2">
            {t('live.sandboxStopped.title')}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {t('live.sandboxStopped.description')}
          </p>
        </div>
      )
    }
    switch (activeSection) {
      case 'overview':
        return (
          <LiveOverviewSection
            project={project}
            processing={processing}
            onRedeploy={onRedeploy}
            activeWorkflow={activeWorkflow}
            onAbortWorkflow={onAbortWorkflow}
            onRetryWorkflow={onRetryWorkflow}
            isAbortingWorkflow={isAbortingWorkflow}
            isRetryingWorkflow={isRetryingWorkflow}
          />
        )
      case 'health':
        return <LiveHealthSection project={project} />
      case 'logs':
        return <LiveLogsSection project={project} />
      case 'scale':
        return <LiveScaleSection project={project} />
      case 'errors':
        return <ErrorsPane projectSlug={project.slug} conversationId={conversationId} />
      case 'database':
        return <LiveDatabaseSection project={project} />
      case 'seo':
        return <LiveSeoSection project={project} conversationId={conversationId} />
      case 'security':
        return <LiveSecurityAuditSection project={project} conversationId={conversationId} />
      case 'integrations':
        return (
          <div className="p-4">
            <LiveIntegrationsSection
              key={`live-integrations-${project.integrations.map(i => `${i.id}-${i.has_live_variables}`).join('-')}`}
              project={project}
            />
          </div>
        )
      case 'dns':
        return (
          <div className="p-4">
            <VanitySubdomainSection project={project} />
            <DnsManagementSection project={project} />
          </div>
        )
      default:
        return null
    }
  }

  // Mobile: bottom tab bar
  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-card">
        <LiveStatusBar
          project={project}
          processing={processing}
          onDeploy={onDeploy}
          onRedeploy={onRedeploy}
          onRestart={onRestart}
          activeWorkflow={activeWorkflow}
        />
        <div className="flex-1 overflow-auto">
          {renderContent()}
        </div>
        <div className="flex items-center justify-around border-t border-border bg-card px-1 py-1.5">
          {allSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-md transition-colors ${
                activeSection === section.id
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <section.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{section.label}</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Desktop: inline right icon sidebar (no shadcn Sidebar — it uses fixed positioning)
  return (
    <div className="flex h-full w-full bg-card">
      {/* Content area */}
      <div className="flex flex-1 flex-col min-w-0">
        <LiveStatusBar
          project={project}
          processing={processing}
          onDeploy={onDeploy}
          onRedeploy={onRedeploy}
          onRestart={onRestart}
          activeWorkflow={activeWorkflow}
        />
        {activeSection === 'database' ? (
          <div className="flex-1 min-h-0">
            {renderContent()}
          </div>
        ) : (
          <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
            {renderContent()}
          </ScrollArea>
        )}
      </div>

      {/* Right icon sidebar — inline, not fixed */}
      <div className="flex flex-col items-center border-l border-border bg-muted/30 py-3 px-1.5 gap-1 w-[3.5rem] flex-shrink-0">
        {/* SRE section icons */}
        {sreSections.map((section) => (
          <IconButton
            key={section.id}
            section={section}
            isActive={activeSection === section.id}
            onClick={() => setActiveSection(section.id)}
          />
        ))}

        {/* Separator between groups */}
        {configSections.length > 0 && (
          <Separator className="my-1.5 w-8" />
        )}

        {/* Config section icons */}
        {configSections.map((section) => (
          <IconButton
            key={section.id}
            section={section}
            isActive={activeSection === section.id}
            onClick={() => setActiveSection(section.id)}
          />
        ))}
      </div>
    </div>
  )
}

function IconButton({
  section,
  isActive,
  onClick,
}: {
  section: SectionDef
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 w-11 h-11 rounded-lg transition-colors ${
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
    >
      <section.icon className="h-5 w-5" />
      <span className="text-[9px] font-medium leading-none">{section.label}</span>
    </button>
  )
}
